import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Wrench, Camera } from "lucide-react";
import api from "../lib/api";
import Layout from "../components/Layout";
import { useAuth } from "../context/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";

const ISSUE_TYPES = ["wrong_text", "wrong_image", "broken_link", "wrong_price", "layout", "navigation", "user_account", "payment", "feature", "mobile_display", "other"];
const ISSUE_STATUSES = ["reported", "triaged", "in_progress", "resolved", "dismissed", "escalated_dev"];
const PRIORITIES = ["low", "medium", "high", "critical"];

const STATUS_STYLES = {
  reported: "bg-champagne/10 text-champagne border-champagne/30",
  triaged: "bg-champagne/15 text-champagne border-champagne/40",
  in_progress: "bg-champagne/15 text-champagne border-champagne/40",
  resolved: "bg-[#4D5B46]/30 text-[#A3C293] border-[#4D5B46]/50",
  dismissed: "bg-elevated text-textmuted border-seam",
  escalated_dev: "bg-[#7a4a30]/20 text-[#E5B493] border-[#7a4a30]/50",
};

const FixCenter = () => {
  const { hasPermission } = useAuth();
  const canTriage = hasPermission("tasks.edit");
  const [issues, setIssues] = useState([]);
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    entity_id: "",
    page_module: "",
    issue_type: "wrong_text",
    priority: "medium",
    description: "",
    screenshot_url: "",
  });

  const load = useCallback(async () => {
    try {
      const [i, e] = await Promise.all([
        api.get("/issues"),
        api.get("/enterprise/entities"),
      ]);
      setIssues(i.data);
      setEntities(e.data);
    } catch {
      toast.error("Could not load issues");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (!payload.entity_id) delete payload.entity_id;
      if (!payload.screenshot_url) delete payload.screenshot_url;
      await api.post("/issues", payload);
      toast.success("Issue reported");
      setOpen(false);
      setForm({ entity_id: "", page_module: "", issue_type: "wrong_text", priority: "medium", description: "", screenshot_url: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Report failed");
    }
  };

  const updateIssue = async (issue, patch) => {
    try {
      await api.patch(`/issues/${issue.id}`, patch);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    }
  };

  return (
    <Layout>
      <section className="mb-6 flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <p className="label-eyebrow">Operations</p>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tight mt-2">Fix Center</h1>
          <p className="text-textmuted mt-3 max-w-2xl leading-relaxed">
            Report and triage anything wrong across R.S.V.P. sites and apps — content, layout,
            broken links, prices, payment. Route configuration issues to Site Editor; code bugs
            escalate to Developer Mode.
          </p>
        </div>
        <button
          onClick={() => setOpen(true)}
          data-testid="report-issue-btn"
          className="inline-flex items-center gap-2 self-start bg-champagne text-obsidian hover:bg-champagne-soft font-medium rounded-md px-5 py-2.5 transition-all"
        >
          <Plus className="w-4 h-4" strokeWidth={1.7} /> Report issue
        </button>
      </section>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-champagne animate-spin" /></div>
      ) : issues.length === 0 ? (
        <div className="regal-card p-12 text-center">
          <Wrench className="w-8 h-8 text-champagne mx-auto mb-3" strokeWidth={1.4} />
          <p className="text-textmuted">No issues reported.</p>
        </div>
      ) : (
        <div className="regal-card overflow-hidden">
          <div className="divide-y divide-seam">
            {issues.map((i) => {
              const configurable = ["wrong_text", "wrong_image", "wrong_price"].includes(i.issue_type);
              return (
                <div key={i.id} data-testid={`issue-row-${i.id}`} className="px-5 py-4 hover:bg-elevated/40">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${STATUS_STYLES[i.status] || STATUS_STYLES.reported}`}>
                          {i.status.replace(/_/g, " ")}
                        </span>
                        <span className="text-[10px] uppercase tracking-wider text-champagne">{i.issue_type.replace(/_/g, " ")}</span>
                        <span className="text-[10px] uppercase tracking-wider text-textmuted">· {i.priority}</span>
                      </div>
                      <p className="text-sm text-textmain mt-2">{i.description}</p>
                      {i.page_module && <p className="text-[11px] text-textmuted mt-1">Location: {i.page_module}</p>}
                    </div>
                    {canTriage && (
                      <div className="flex gap-2 flex-wrap">
                        <select
                          value={i.status}
                          onChange={(e) => updateIssue(i, { status: e.target.value })}
                          className="bg-obsidian border border-seam rounded-md px-2 py-1 text-xs text-textmuted focus:outline-none focus:border-champagne"
                        >
                          {ISSUE_STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                        </select>
                        {configurable && i.entity_id && (
                          <a
                            href={`/editor?entity=${i.entity_id}`}
                            className="text-xs border border-champagne/40 text-champagne px-3 py-1 rounded hover:bg-champagne/10 inline-flex items-center gap-1"
                          >
                            Edit this area
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-surface border-seam max-w-lg text-textmain">
          <DialogHeader>
            <DialogTitle className="font-heading text-3xl">Report an issue</DialogTitle>
            <DialogDescription className="text-textmuted">
              Tell us what's wrong. If it's a text, image or price issue we can jump you to Site Editor.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3 mt-2">
            <div>
              <label className="block label-eyebrow mb-1">Business</label>
              <select value={form.entity_id} onChange={(e) => setForm({ ...form, entity_id: e.target.value })} className="w-full bg-obsidian border border-seam rounded-md px-3 py-2 text-sm">
                <option value="">— None</option>
                {entities.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block label-eyebrow mb-1">Type</label>
                <select value={form.issue_type} onChange={(e) => setForm({ ...form, issue_type: e.target.value })} className="w-full bg-obsidian border border-seam rounded-md px-3 py-2 text-sm">
                  {ISSUE_TYPES.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
                </select>
              </div>
              <div>
                <label className="block label-eyebrow mb-1">Priority</label>
                <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="w-full bg-obsidian border border-seam rounded-md px-3 py-2 text-sm">
                  {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="block label-eyebrow mb-1">Page or module</label>
              <input value={form.page_module} onChange={(e) => setForm({ ...form, page_module: e.target.value })} className="w-full bg-obsidian border border-seam rounded-md px-3 py-2 text-sm" placeholder="e.g. Homepage / Pricing section" />
            </div>
            <div>
              <label className="block label-eyebrow mb-1">Description</label>
              <textarea required rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="issue-desc" className="w-full bg-obsidian border border-seam rounded-md px-3 py-2 text-sm resize-none" />
            </div>
            <div>
              <label className="block label-eyebrow mb-1 flex items-center gap-1"><Camera className="w-3 h-3" /> Screenshot URL (optional)</label>
              <input value={form.screenshot_url} onChange={(e) => setForm({ ...form, screenshot_url: e.target.value })} className="w-full bg-obsidian border border-seam rounded-md px-3 py-2 text-sm" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 rounded-md border border-seam text-textmuted hover:text-textmain text-sm">Cancel</button>
              <button type="submit" data-testid="issue-submit" className="px-5 py-2 rounded-md bg-champagne text-obsidian text-sm font-medium hover:bg-champagne-soft">Report</button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default FixCenter;
